import api from "./api";

/**
 * AI Cost API.
 *
 * Every endpoint here is org-scoped by studio-backend from the caller's JWT —
 * the `orgId` argument is a hint the backend validates, never a filter it
 * trusts. A user who asks for an org outside their scope gets a 403, not
 * someone else's numbers.
 */

const AI_COST = "/ai-cost";

/** Turn a filters object into repeated `filter[dim]=value` params. */
const withFilters = (params = {}, filters = {}) => {
  const out = { ...params };
  Object.entries(filters).forEach(([dim, value]) => {
    if (value === undefined || value === null || value === "") return;
    out[`filter[${dim}]`] = Array.isArray(value) ? value.join(",") : value;
  });
  return out;
};

/**
 * Headline numbers for the dashboard hero: current period, the previous
 * comparable period, unattributed share, cache savings and a month-end
 * projection — in one round-trip.
 */
export const fetchAICostOverview = async ({ orgId, from, to, filters } = {}) => {
  const response = await api.get(`${AI_COST}/overview`, {
    params: withFilters({ org_id: orgId, from, to }, filters),
  });
  return response.data;
};

/**
 * Spend grouped by one or two dimensions, with the previous period alongside.
 * `groupBy` is a dimension name or a comma-separated pair for a cross-tab
 * (e.g. "model,workload").
 */
export const fetchAICostSummary = async ({ orgId, groupBy, from, to, filters, compare } = {}) => {
  const response = await api.get(`${AI_COST}/summary`, {
    params: withFilters(
      { org_id: orgId, group_by: groupBy, from, to, compare },
      filters
    ),
  });
  return response.data;
};

/**
 * Spend over time — the trend series. `interval` is day|week|month; an optional
 * `groupBy` splits it into one series per key, with the long tail folded into
 * `__other__` rather than dropped.
 */
export const fetchAICostTimeseries = async ({
  orgId,
  from,
  to,
  interval = "day",
  groupBy,
  seriesLimit,
  filters,
} = {}) => {
  const response = await api.get(`${AI_COST}/timeseries`, {
    params: withFilters(
      { org_id: orgId, from, to, interval, group_by: groupBy, series_limit: seriesLimit },
      filters
    ),
  });
  return response.data;
};

/** Raw per-call drill-down. Reads the event view, not the daily rollup. */
export const fetchAICostCalls = async ({
  orgId,
  from,
  to,
  filters,
  sessionId,
  limit = 100,
  offset = 0,
} = {}) => {
  const response = await api.get(`${AI_COST}/calls`, {
    params: withFilters(
      { org_id: orgId, from, to, session_id: sessionId, limit, offset },
      filters
    ),
  });
  return response.data;
};

/**
 * Model + meter rates in effect, plus every model the ledger has seen with no
 * rate at all — that last list is spend currently being recorded as zero.
 */
export const fetchAICostRateCard = async ({ orgId } = {}) => {
  const response = await api.get(`${AI_COST}/rate-card`, { params: { org_id: orgId } });
  return response.data;
};

/** Budgets with current-period spend, % consumed and a projected total. */
export const fetchAICostBudgets = async ({ orgId } = {}) => {
  const response = await api.get(`${AI_COST}/budgets`, { params: { org_id: orgId } });
  return response.data;
};

export const fetchAICostAlerts = async ({ orgId } = {}) => {
  const response = await api.get(`${AI_COST}/alerts`, { params: { org_id: orgId } });
  return response.data;
};

export const createAICostBudget = async (budget) => {
  const response = await api.post(`${AI_COST}/budgets`, budget);
  return response.data;
};

export const updateAICostBudget = async (budgetId, budget) => {
  const response = await api.put(`${AI_COST}/budgets/${budgetId}`, budget);
  return response.data;
};

export const deleteAICostBudget = async (budgetId) => {
  const response = await api.delete(`${AI_COST}/budgets/${budgetId}`);
  return response.data;
};
